import { useState, useEffect } from "react";
import type { ColumnsType } from "antd/es/table";
import TableList from "../../ui-component/Table";
import { Header } from "../../components/Header";
import { useGetData } from "../../Network/useGetQueryData";
import { useLocation, useNavigate } from "react-router-dom";
import { uniqBy } from "lodash";
import { RequestSvg } from "../../components/sideMenu/SidebarSVG/RequestSvg";
import dayjs from "dayjs";
import "./style.scss";
import { FilterIcon } from "../../Icons/FilterIcon";
import { Button, DatePicker, Dropdown, Menu, message, Tooltip } from "antd";
import { useSelector } from "react-redux";
import { DotsMenu } from "../../Icons/DotsMenu";
import { useTranslation } from "react-i18next";
import { statusMap } from "../../utils/requestStatus";
import apiClient from "../../Network";
import { UserRolesId } from "../enum";
import { ReasonNoteModal } from "./viewRequests/ReasonModal";

type Props = {
  isResults?: boolean;
  isApprovalPage?: boolean;
};

const RequestsList = ({ isResults, isApprovalPage }: Props) => {
  const { t, i18n } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.user) || {};
  const [offset, setOffset] = useState(0);
  const [searchKey, setSearchKey] = useState<string>();
  const [SearchLoading, setSearchLoading] = useState<boolean>();
  const [reasonModal, setReasonModal] = useState<{
    isOpen: boolean;
    url: string;
  }>();
  const [searchId, setSearchId] = useState<string[]>();
  const [searchCustomerId, setSearchCustomerId] = useState<[]>();
  const [filteredStatus, setFilteredStatus] = useState<string | null>();
  const [requestedBy, setRequestedBy] = useState<string | null>();
  const [requests, setRequests] = useState<any>([]);
  const [createdAtRange, setCreatedAtRange] = useState<string[] | null>();
  const [totalCount, setTotalCount] = useState<any>(0);

  const [limit] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();
  const { RangePicker } = DatePicker;

  const { serviceProviderId } =
    useSelector((state: any) => state.user.user) || {};

  const { data, isLoading, refetch } = useGetData<{
    data: { items: any[]; count: number };
  }>({
    queryKey: [
      "requests",
      location,
      {
        offset,
        limit,
        searchKey,
        createdAt: 1,
        createdAtRange,
        filteredStatus,
        requestedBy,
        searchCustomerId,
        searchId,
      },
    ],
    url: "/service-request",
    params: {
      offset,
      limit,
      withDeleted: false,
      serviceProviderId,
      sortKey: "createdAt:-1",
      createdAtFrom: createdAtRange?.[0],
      createdAtTo: createdAtRange?.[1],
      status: isResults ? "completed" : filteredStatus,
      requestedBy,
      ids: searchId,
      customerId: searchCustomerId,
      ...(!!searchKey ? { searchKey } : {}),
      ...(!!isResults ? { hasResult: true } : {}),
      ...(!!searchKey
        ? { searchOn: "ServiceRequestSearchOn.CUSTOMER_PHONE" }
        : {}),
    },
  });

  const handleAction = async (url: string, body?: any) => {
    try {
      await apiClient.post(`service-request${url}`, body);
      refetch();
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Can't Change Request Status",
      );
    }
  };

  function handleConcatServices(record: any): string {
    const names: string[] = (record.children || []).reduce(
      (acc: string[], child: any) => {
        const svcNames = (child.services || [])
          .map((s: any) => s?.service?.name?.[i18n.language || "en"])
          .filter(Boolean) as string[];
        return acc.concat(svcNames);
      },
      [],
    );

    const unique = Array.from(new Set(names));
    return unique.join(", ");
  }

  const requestsColumns: ColumnsType<any> = [
    {
      title: t("requests.requestId"),
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <div
          className="title-request"
          onClick={() => navigate(`/request/${record?.id}`)}
        >
          <span>
            <RequestSvg />
          </span>
          <p>{record.id}</p>
        </div>
      ),
    },
    {
      title: t("requests.requester"),
      dataIndex: "requestedBy",
      key: "requestedBy",
      filterDropdown: ({ confirm, clearFilters }) => (
        <Menu className="filtered-menu">
          {["customer", "admin", "serviceProvider"].map((value) => (
            <Menu.Item
              key={value}
              onClick={() => {
                setRequests([]);
                setRequestedBy(value);
                confirm();
              }}
            >
              {t(`requests.${value}`)}
            </Menu.Item>
          ))}

          <Menu.Divider />

          <Menu.Item
            key="reset"
            onClick={() => {
              setRequests([]);
              setRequestedBy(null);
              clearFilters?.();
              confirm();
            }}
          >
            <Button type="link">{t("shared.reset")}</Button>
          </Menu.Item>
        </Menu>
      ),
      render: (_: any, record: any) => (
        <p className="avatar-text" style={{ textTransform: "capitalize" }}>
          {record.requestedBy}
        </p>
      ),
    },
    {
      title: t("requests.requesterTo"),
      dataIndex: "customer",
      key: "customer",
      render: (_: any, record: any) => (
        <Tooltip title={record?.customer?.user?.firstName}>
          <p className="avatar-text" style={{ textTransform: "capitalize" }}>
            {record?.customer?.user?.firstName}
          </p>
        </Tooltip>
      ),
    },
    ...(isApprovalPage
      ? []
      : [
          {
            title: t("requests.cost"),
            dataIndex: "cost",
            key: "cost",
            render: (_: any, record: any) => (
              <p>{record.totalAmountAfterDiscount} L.E.</p>
            ),
          },
        ]),
    {
      title: t("requests.provider"),
      dataIndex: "provider",
      key: "provider",
      render: (_: any, record: any) => (
        <p>
          {record.children?.[0]?.serviceProvider?.name?.[
            i18n.language || "en"
          ] || "-"}
        </p>
      ),
    },
    ...(isApprovalPage
      ? []
      : [
          {
            title: t("requests.plan"),
            dataIndex: "paymentMethod",
            key: "paymentMethod",
            render: (_: any, record: any) => (
              <p style={{ textTransform: "capitalize" }}>
                {record.paymentMethod}
              </p>
            ),
          },
        ]),
    ...(isApprovalPage
      ? []
      : [
          {
            title: t("form.category"),
            dataIndex: "paymentMethod",
            key: "paymentMethod",
            render: (_: any, record: any) => (
              <p style={{ textTransform: "capitalize" }}>
                {record?.children?.[0]?.services?.[0]?.service?.type || "-"}
              </p>
            ),
          },
        ]),
    {
      title: t("requests.services"),
      dataIndex: "services",
      key: "services",
      render: (_, record) => (
        <Tooltip title={handleConcatServices(record)}>
          <p style={{ direction: i18n.language === "en" ? "ltr" : "rtl" }}>
            {handleConcatServices(record)}
          </p>
        </Tooltip>
      ),
    },
    {
      title: t("requests.status"),
      dataIndex: "status",
      key: "status",
      filterDropdown: ({ confirm, clearFilters }) =>
        isResults ? null : (
          <Menu className="filtered-menu">
            {[
              "pending",
              "completed",
              "cancelled",
              "rejected",
              "waitingAdminApproval",
              "waitingPayment",
              "waitingCustomerApproval",
              "inProgress",
            ].map((value) => (
              <Menu.Item
                key={value}
                onClick={() => {
                  setRequests([]);
                  setFilteredStatus(value);
                  confirm();
                }}
              >
                {t(`requests.${value}`)}
              </Menu.Item>
            ))}
            <Menu.Divider />

            <Menu.Item
              key="reset"
              onClick={() => {
                setRequests([]);
                setFilteredStatus(null);
                clearFilters?.();
                confirm();
              }}
            >
              <Button type="link">{t("shared.reset")}</Button>
            </Menu.Item>
          </Menu>
        ),
      render: (_, record) => (
        <Tooltip title={statusMap(record.status)}>
          <p
            className={`request-list-status ${record.status}`}
            style={{ textTransform: "capitalize" }}
          >
            {statusMap(record.status, record?.isPaid)}
          </p>
        </Tooltip>
      ),
    },
    {
      title: t("requests.requestsOn"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (_, record) => (
        <p>{dayjs(record.createdAt).format("DD/MM/YYYY")}</p>
      ),
    },
    ...(isApprovalPage
      ? [
          {
            title: t("requests.notes"),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (_: any, record: any) => (
              <p>
                {" "}
                {record.cancellationNote ||
                  record?.cancellationReason ||
                  record.rejectionNote ||
                  record.rejectionReason}
              </p>
            ),
          },
        ]
      : []),
    ...(isApprovalPage
      ? []
      : [
          {
            title: "",
            dataIndex: "",
            key: "actions",
            render: (_: any, record: any) => {
              const statusMenuList = [
                ...(record?.status === "waitingAdminApproval" ||
                record?.status === "waitingCustomerApproval"
                  ? [
                      {
                        title: t("viewRequest.approve"),
                        action: (id: string | number) =>
                          setReasonModal({
                            isOpen: true,
                            url: `/${id}/approve`,
                          }),
                      },
                      {
                        title: t("viewRequest.reject"),
                        action: (id: string | number) =>
                          setReasonModal({
                            isOpen: true,
                            url: `/${id}/reject`,
                          }),
                      },
                    ]
                  : []),
                ...(record?.status === "waitingPayment" &&
                record?.paymentMethod !== "cash"
                  ? [
                      {
                        title: t("viewRequest.changePayment"),
                        action: (id: string | number) =>
                          handleAction(`/change-payment-method`, {
                            serviceRequestId: id,
                            paymentMethod: "cash",
                          }),
                      },
                    ]
                  : []),
                ...(record?.status === "inProgress" || record?.isPaid
                  ? [
                      {
                        title: t("viewRequest.complete"),
                        action: (id: string | number) =>
                          setReasonModal({
                            isOpen: true,
                            url: `/${id}/complete`,
                          }),
                      },
                    ]
                  : []),
                {
                  title: t("viewRequest.cancel"),
                  action: (id: string | number) =>
                    setReasonModal({ isOpen: true, url: `/${id}/cancel` }),
                },
              ];

              const isPrescriptionUrl = [
                {
                  label: (
                    <span onClick={() => navigate(`/add-request/${record.id}`)}>
                      {t("viewRequest.addServices")}
                    </span>
                  ),
                  key: "1",
                },
              ];

              const menuItems = statusMenuList?.map((stat, index) => ({
                key: index.toString(),
                label: (
                  <span onClick={() => stat.action(record?.id)}>
                    {t(stat?.title)}
                  </span>
                ),
              }));

              if (userInfo?.roleId === UserRolesId.Provider) return;
              return (
                <div>
                  <Dropdown
                    menu={{
                      items:
                        !!record?.prescriptionUrl && !record?.children?.length
                          ? isPrescriptionUrl
                          : menuItems,
                    }}
                    trigger={["click"]}
                  >
                    <span style={{ cursor: "pointer" }}>
                      <DotsMenu />
                    </span>
                  </Dropdown>
                </div>
              );
            },
          },
        ]),
  ];

  useEffect(() => {
    if (
      data?.data?.items &&
      (!!searchKey ||
        (!!createdAtRange?.[0] && !!createdAtRange?.[1]) ||
        filteredStatus ||
        requestedBy ||
        searchCustomerId ||
        searchId)
    ) {
      setRequests(data.data.items);
      setSearchLoading(false);
      return;
    }

    if (data?.data?.items) {
      setRequests((prevProviders: any) => [
        ...prevProviders,
        ...data.data.items,
      ]);
      setTotalCount(data?.data?.count);
    }
  }, [data]);

  const loadMoreItems = () => {
    if (
      data &&
      requests.length < data.data.count &&
      !isLoading &&
      createdAtRange?.length !== 2 &&
      !filteredStatus &&
      !requestedBy
    ) {
      setOffset((prevOffset) => prevOffset + 1);
    }
  };

  return (
    <div className="requests-list-wrapper">
      <Header
        title={t("requests.title")}
        headerAction={{
          actionTitle: t("requests.add"),
          onAction: () => (window.location.href = "/add-request"),
        }}
        searchMenu
        search={{
          placeholder: t("requests.search"),
          btnLoading: SearchLoading,
          searchAction: (val) => {
            offset > 0 && setOffset(0);
            setSearchLoading(true);

            if (!!!val) {
              setSearchKey(undefined);
              setSearchCustomerId(undefined);
              setSearchId(undefined);
              setRequests([]);
            }
            setTimeout(() => {
              if (val?.reqId) setSearchId(val?.reqId?.value);
              if (val?.medHubId) setSearchCustomerId(val?.medHubId?.value);
              if (val?.phone) setSearchKey(val?.phone?.value);
            }, 300);
          },
        }}
      />
      <div className="header-filter-wrapper">
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <p className="filter-title">
            <FilterIcon />
            {t("requests.filter")}
          </p>

          <div className="">
            <RangePicker
              onChange={(_, dates) => {
                if (!!!dates?.[0] || !!!dates?.[1]) {
                  setRequests([]);
                  setCreatedAtRange(null);
                  return;
                }
                setRequests([]);
                setCreatedAtRange(dates);
              }}
              format={"YYYY-MM-DD"}
            />
          </div>
        </div>
        <p className="filter-count">
          {t("requests.filtered")} <span>{requests?.length}</span>{" "}
          {t("requests.from")} <span>{totalCount}</span>
        </p>
      </div>
      <TableList
        data={uniqBy(requests, "id")}
        columns={requestsColumns}
        loading={!!searchKey && !!isLoading}
        rowKey="id"
        loadMore={loadMoreItems}
        isLoadMore={isLoading}
        height="calc(-300px + 100vh)"
        expandable={undefined}
      />
      {reasonModal?.isOpen && (
        <ReasonNoteModal
          url={reasonModal.url}
          hasReason={
            reasonModal.url?.includes("cancel") ||
            reasonModal.url?.includes("reject")
          }
          onCloseModal={() => setReasonModal({ isOpen: false, url: "" })}
          refetch={refetch}
        />
      )}
    </div>
  );
};

export default RequestsList;
